import { useWhiteboardStore } from "@/stores/whiteboardStore";
import { HandwrittenText } from "./HandwrittenText";

/**
 * The whiteboard surface. Phase 0/1: renders handwritten titles/content
 * via HandwrittenText so the realism layer is visible from the very
 * first demo, even before the real canvas/stroke engine (Phase 1-2)
 * replaces this with true freehand + KaTeX + diagram rendering.
 *
 * Coordinate system for real strokes will be normalized 0-1 space
 * (see whiteboardStore) so phone/tablet/desktop render identically.
 */
export function Whiteboard({
  title,
  body,
  highlightedWord,
}: {
  title?: string;
  body?: string;
  highlightedWord?: string;
}) {
  const strokes = useWhiteboardStore((s) => s.strokes);
  const storeTitle = useWhiteboardStore((s) => s.title);
  const storeBody = useWhiteboardStore((s) => s.body);
  const storeHighlight = useWhiteboardStore((s) => s.highlightedWord);

  // Props (Landing page demo) win when given; otherwise the board reflects
  // live lesson state written by the lesson runner via whiteboardStore.
  title = title ?? storeTitle ?? undefined;
  body = body ?? storeBody ?? undefined;
  highlightedWord = highlightedWord ?? storeHighlight ?? undefined;

  return (
    <div className="relative aspect-[4/3] w-full rounded-2xl bg-board p-6 sm:p-10 shadow-glass overflow-hidden">
      {/* subtle board texture, not decoration for its own sake — grounds the "real classroom" cue */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_1px_1px,#fff_1px,transparent_0)] bg-[length:16px_16px]" />

      {title && (
        <h2 className="handwritten text-4xl sm:text-5xl text-chalk mb-4">
          <HandwrittenText text={title} />
        </h2>
      )}
      {body && (
        <p className="handwritten text-2xl sm:text-3xl leading-relaxed text-chalk/90">
          <HandwrittenText text={body} highlighted={!!highlightedWord} />
        </p>
      )}
      {!title && !body && strokes.length === 0 && (
        <p className="text-chalk/40 font-body text-sm">Board is blank — waiting for the lesson to begin.</p>
      )}
    </div>
  );
}
